// components/LensView.tsx

import React, { useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks.ts";

import {
	Box,
	Button,
	ClickAwayListener,
	List,
	ListItem,
	ListItemText,
	Typography,
} from "@mui/material";

import { ColorPicker, useColor } from "react-color-palette";
import "react-color-palette/css";
import ReactDOM from "react-dom";

import { updateColorConfig } from "../../../store/slices/userSlice.ts";
import globalAxios from "../../../globalAxios.js";

import {
	setSnackbarOpen,
	setSnackbarSeverity,
	setSnackbarText,
} from "../../../store/slices/snackbarSlice.ts";

import {
	SPECIALTY_OPTIONS_BASE,
	UNCATEGORISED,
} from "../../../constants/index.ts";

type LegendItem = {
	label: string;
	color: string;
	description?: string;
};

const Legend = () => {
	const dispatch = useAppDispatch();

	const currentLens = useAppSelector((state) => state.calendar.lens);
	const userSettings = useAppSelector((state) => state.user.settings);

	const initialColors = userSettings?.calendar?.colorConfig || {};

	const calendarEvents = useAppSelector(
		(state) => state.calendar?.sessions || [],
	);

	const [selectedColor, setSelectedColor] = useColor("#1976d2");
	const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

	const [colorConfig, setColorConfig] =
		useState<Record<string, Record<string, string>>>(initialColors);

	const [isPickerOpen, setIsPickerOpen] = useState(false);

	const [pickerPosition, setPickerPosition] = useState<{
		top: number;
		left: number;
	}>({
		top: 0,
		left: 0,
	});

	const pickerRef = useRef<HTMLDivElement | null>(null);

	/* =============================
	 * HELPERS
	 * ============================= */

	const normaliseSpec = (s: unknown): string =>
		String(s ?? "").trim() || UNCATEGORISED;

	/*
	 * Returns the currently configured colour for a legend item.
	 *
	 * Funding Status includes fallback support for the old two-tier
	 * configuration so existing user colour settings aren't lost.
	 */
	const getConfiguredColour = (item: LegendItem) => {
		const configuredColour = colorConfig?.[currentLens]?.[item.label];

		if (configuredColour) {
			return configuredColour;
		}

		if (currentLens === "fundingStatus") {
			const fundingConfig = colorConfig?.fundingStatus || {};

			switch (item.label) {
				case "Funding Incomplete":
					return fundingConfig["Funding Missing"] || item.color;

				case "Funding Complete":
					return fundingConfig["Funding Available"] || item.color;

				case "IFC Required":
					return item.color;

				default:
					return item.color;
			}
		}

		return item.color;
	};

	/* =============================
	 * SPECIALITIES
	 * ============================= */

	const activeSpecialities = useMemo(() => {
		const set = new Set<string>();
		let sawUncategorised = false;

		for (const ev of calendarEvents as any[]) {
			const arr = ev?.surgeon?.specialty;

			if (Array.isArray(arr) && arr.length) {
				const primary = normaliseSpec(arr[0]);

				if (primary) {
					set.add(primary);
				}
			} else {
				sawUncategorised = true;
			}
		}

		const baseSet = new Set([
			...(SPECIALTY_OPTIONS_BASE ?? []),
			UNCATEGORISED,
		]);

		const present = [...set].filter((name) => baseSet.has(name));

		present.sort((a, b) => {
			const aUncategorised = a === UNCATEGORISED;
			const bUncategorised = b === UNCATEGORISED;

			if (aUncategorised && !bUncategorised) return 1;
			if (bUncategorised && !aUncategorised) return -1;

			return a.localeCompare(b);
		});

		if (sawUncategorised && !present.includes(UNCATEGORISED)) {
			present.push(UNCATEGORISED);
		}

		return present;
	}, [calendarEvents]);

	/* =============================
	 * LEGEND
	 * ============================= */

	const legend: LegendItem[] = useMemo(() => {
		switch (currentLens) {
			case "main":
				return [
					{
						label: "Work Regular",
						color: "#1976d2",
					},
					{
						label: "Work Adhoc",
						color: "#003f7d",
					},
					{
						label: "Personal Regular",
						color: "#3ccf44",
					},
					{
						label: "Personal Adhoc",
						color: "#19771d",
					},
				];

			case "uploadStatus":
				return [
					{
						label: "Upload Complete",
						color: "#388e3c",
					},
					{
						label: "Upload Pending",
						color: "#f57c00",
					},
				];

			case "feeEstimate":
				return [
					{
						label: "Estimate",
						color: "#388e3c",
					},
					{
						label: "No Estimate",
						color: "grey",
					},
				];

			case "reconciliation":
				return [
					{
						label: "Reconciled",
						color: "#388e3c",
					},
					{
						label: "Not Reconciled",
						color: "grey",
					},
				];

			case "fundingStatus":
				return [
					{
						label: "IFC Required",
						color: "#d32f2f",
						description: "IFC required but not completed",
					},
					{
						label: "Funding Incomplete",
						color: "#ed6c02",
						description:
							"Medicare or health fund details incomplete",
					},
					{
						label: "Funding Complete",
						color: "#388e3c",
						description: "Funding requirements complete",
					},
				];

			case "speciality": {
				const cfg = colorConfig?.speciality || {};

				const list = activeSpecialities.length
					? activeSpecialities
					: [];

				return list.map((name) => ({
					label: name,
					color: cfg[name] || "grey",
				}));
			}

			case "tag":
				return [];

			case "IFC":
				return [
					{
						label: "IFC Sent",
						color: "#388e3c",
					},
					{
						label: "IFC Not Sent",
						color: "grey",
					},
				];

			default:
				return [];
		}
	}, [currentLens, colorConfig, activeSpecialities]);

	/* =============================
	 * COLOUR PICKER
	 * ============================= */

	const handleColorBoxClick = (event: any, item: LegendItem) => {
		setSelectedLabel(item.label);

		const currentHex = getConfiguredColour(item);

		const rgb = hexToRgb(currentHex);
		const hsv = rgbToHsv(rgb);

		setSelectedColor({
			hex: currentHex,
			rgb,
			hsv,
		});

		const rect = event.currentTarget.getBoundingClientRect();

		setPickerPosition({
			top: rect.top,
			left: rect.left + 30,
		});

		setIsPickerOpen(true);
	};

	const handleClickAway = (event: any) => {
		if (
			pickerRef.current &&
			pickerRef.current.contains(event.target as Node)
		) {
			return;
		}

		setIsPickerOpen(false);
	};

	const handleSaveColor = async () => {
		if (!selectedLabel) return;

		const newHex = selectedColor.hex;

		const newColorConfig = {
			...colorConfig,

			[currentLens]: {
				...(colorConfig[currentLens] || {}),

				[selectedLabel]: newHex,
			},
		};

		console.debug("[Legend] saving colour:", {
			lens: currentLens,
			label: selectedLabel,
			colour: newHex,
		});

		setColorConfig(newColorConfig);
		setIsPickerOpen(false);

		dispatch(updateColorConfig(newColorConfig));

		try {
			await globalAxios.post("/user/settings/calendar/color-config", {
				colorConfig: newColorConfig,
			});

			console.debug("[Legend] colour configuration saved");

			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("success"));
			dispatch(setSnackbarText("Update saved!"));
		} catch (error) {
			console.error(
				"[Legend] failed to save colour configuration:",
				error,
			);

			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("error"));

			dispatch(
				setSnackbarText(`❌ Error updating colour settings: ${error}`),
			);
		}
	};

	/* =============================
	 * RENDER
	 * ============================= */

	return (
		<Box>
			<Typography
				variant="body1"
				gutterBottom
				fontWeight={600}
				textAlign="center"
			>
				Legend
			</Typography>

			<List
				sx={{
					maxHeight: currentLens === "speciality" ? 320 : "auto",
					overflowY: "auto",
				}}
			>
				{legend.map((item) => {
					const displayColour = getConfiguredColour(item);

					return (
						<ListItem
							key={item.label}
							sx={{
								display: "flex",
								alignItems: "center",
							}}
						>
							{/* Colour picker trigger */}
							<Box
								sx={{
									width: 20,
									height: 20,
									flexShrink: 0,
									backgroundColor: displayColour,
									marginRight: 2,
									borderRadius: "50%",
									cursor: "pointer",
									border: "1px solid rgba(0,0,0,0.2)",
								}}
								onClick={(e) => handleColorBoxClick(e, item)}
								title="Click to change colour"
							/>

							<ListItemText
								primary={item.label}
								secondary={item.description}
								slotProps={{
									secondary: {
										sx: {
											fontSize: "0.72rem",
											lineHeight: 1.2,
										},
									},
								}}
							/>
						</ListItem>
					);
				})}

				{currentLens === "speciality" && legend.length === 0 && (
					<ListItem>
						<ListItemText
							primary={
								<Typography
									variant="body2"
									color="text.secondary"
								>
									No specialities in the current view.
								</Typography>
							}
						/>
					</ListItem>
				)}
			</List>

			{isPickerOpen &&
				ReactDOM.createPortal(
					<ClickAwayListener onClickAway={handleClickAway}>
						<Box
							ref={pickerRef}
							sx={{
								position: "fixed",
								top: pickerPosition.top,
								left: pickerPosition.left,
								backgroundColor: "white",
								padding: 2,
								paddingTop: 1,
								zIndex: 9999,
								boxShadow: 3,
								borderRadius: 1,
							}}
						>
							<Typography
								fontWeight="bold"
								variant="subtitle1"
								gutterBottom
							>
								{selectedLabel}
							</Typography>

							<ColorPicker
								hideInput={["rgb", "hsv"]}
								color={selectedColor}
								onChange={setSelectedColor}
							/>

							<Button
								disableElevation
								size="small"
								variant="contained"
								sx={{ mt: 1 }}
								onClick={handleSaveColor}
							>
								Save Changes
							</Button>
						</Box>
					</ClickAwayListener>,
					document.body,
				)}
		</Box>
	);
};

export default Legend;

/* =============================
 * COLOUR HELPERS
 * ============================= */

const hexToRgb = (hex: string) => {
	let r = 0;
	let g = 0;
	let b = 0;

	if (hex?.length === 7) {
		r = parseInt(hex.slice(1, 3), 16);
		g = parseInt(hex.slice(3, 5), 16);
		b = parseInt(hex.slice(5, 7), 16);
	}

	return {
		r,
		g,
		b,
		a: 1,
	};
};

const rgbToHsv = ({ r, g, b }: { r: number; g: number; b: number }) => {
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);

	let h = 0;
	let s = 0;
	const v = max;

	const d = max - min;

	s = max === 0 ? 0 : d / max;

	if (max !== min) {
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;

			case g:
				h = (b - r) / d + 2;
				break;

			case b:
				h = (r - g) / d + 4;
				break;
		}

		h /= 6;
	}

	return {
		h: h * 360,
		s: s * 100,
		v: (v / 255) * 100,
		a: 1,
	};
};
